import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { adminAPI } from '../../services/api';
import AdminLayout from '../../components/admin/AdminLayout';
import ConfirmModal from '../../components/common/ConfirmModal';
import {
  FolderOpen,
  Plus,
  Edit2,
  Trash2,
  X,
  Loader,
  CheckCircle,
  AlertCircle,
  ChevronLeft,
  Upload,
  FileText,
  Save,
  Edit3
} from 'lucide-react';

function AreasPage() {
  const { id: projectId } = useParams();
  const fileInputRef = useRef(null);

  const [project, setProject] = useState(null);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingArea, setEditingArea] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  // Document states
  const [selectedArea, setSelectedArea] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [editDocModal, setEditDocModal] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [docContent, setDocContent] = useState('');
  const [loadingDoc, setLoadingDoc] = useState(false);
  const [deleteAreaModal, setDeleteAreaModal] = useState({ open: false, area: null });
  const [deleteDocModal, setDeleteDocModal] = useState({ open: false, doc: null });

  useEffect(() => {
    fetchProject();
  }, [projectId]);

  useEffect(() => {
    if (selectedArea) {
      fetchDocuments(selectedArea.id);
    }
  }, [selectedArea]);

  const fetchProject = async () => {
    try {
      const res = await adminAPI.getProject(projectId);
      setProject(res.data.project);
      setAreas(res.data.areas || []);
      if (res.data.areas?.length > 0 && !selectedArea) {
        setSelectedArea(res.data.areas[0]);
      }
    } catch (error) {
      showToast('Error al cargar proyecto', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchDocuments = async (areaId) => {
    try {
      const res = await adminAPI.getAreaDocuments(areaId);
      setDocuments(res.data.documents || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 1500);
  };

  const openCreateAreaModal = () => {
    setEditingArea(null);
    setFormData({ name: '', description: '' });
    setModalOpen(true);
  };

  const openEditAreaModal = (area) => {
    setEditingArea(area);
    setFormData({ name: area.name, description: area.description || '' });
    setModalOpen(true);
  };

  const handleAreaSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingArea) {
        await adminAPI.updateArea(editingArea.id, formData);
        showToast('Área actualizada correctamente');
      } else {
        const res = await adminAPI.createArea(projectId, formData);
        if (!selectedArea) {
          setSelectedArea(res.data.area);
        }
        showToast('Área creada correctamente');
      }
      setModalOpen(false);
      fetchProject();
    } catch (error) {
      showToast(error.response?.data?.error || 'Error al guardar área', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteArea = (area) => {
    setDeleteAreaModal({ open: true, area });
  };

  const confirmDeleteArea = async () => {
    const area = deleteAreaModal.area;
    setDeleteAreaModal({ open: false, area: null });

    try {
      await adminAPI.deleteArea(area.id);
      showToast('Área eliminada correctamente');
      if (selectedArea?.id === area.id) {
        setSelectedArea(null);
        setDocuments([]);
      }
      fetchProject();
    } catch (error) {
      showToast(error.response?.data?.error || 'Error al eliminar área', 'error');
    }
  };

  const handleFileUpload = async (file) => {
    if (!file || !selectedArea) return;

    setUploading(true);
    try {
      await adminAPI.uploadDocument(selectedArea.id, file);
      showToast(`Documento "${file.name}" subido correctamente`);
      fetchDocuments(selectedArea.id);
      fetchProject();
    } catch (error) {
      showToast(error.response?.data?.error || 'Error al subir documento', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = (doc) => {
    setDeleteDocModal({ open: true, doc });
  };

  const confirmDeleteDocument = async () => {
    const doc = deleteDocModal.doc;
    setDeleteDocModal({ open: false, doc: null });

    try {
      await adminAPI.deleteDocument(doc.id);
      showToast('Documento eliminado correctamente');
      fetchDocuments(selectedArea.id);
      fetchProject();
    } catch (error) {
      showToast('Error al eliminar documento', 'error');
    }
  };

  const openEditDocModal = async (doc) => {
    setEditingDoc(doc);
    setEditDocModal(true);
    setLoadingDoc(true);

    try {
      const res = await adminAPI.getDocumentContent(doc.id);
      setDocContent(res.data.content || '');
    } catch (error) {
      showToast('Error al cargar documento', 'error');
      setEditDocModal(false);
    } finally {
      setLoadingDoc(false);
    }
  };

  const handleSaveDocument = async () => {
    if (!editingDoc) return;
    setSaving(true);

    try {
      await adminAPI.updateDocumentContent(editingDoc.id, docContent);
      showToast('Documento actualizado correctamente');
      setEditDocModal(false);
      fetchDocuments(selectedArea.id);
      fetchProject();
    } catch (error) {
      showToast('Error al guardar documento', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout
      title={
        <div className="header-with-back">
          <Link to="/admin/projects" className="back-link">
            <ChevronLeft size={20} />
          </Link>
          <h1>{project?.name || 'Cargando...'}</h1>
        </div>
      }
    >
      {loading ? (
        <div className="admin-loading">
          <Loader size={32} className="loading-spinner" />
        </div>
      ) : (
        <div className="areas-layout">
          {/* Areas list */}
          <div className="areas-sidebar">
            <div className="areas-sidebar-header">
              <h3>Áreas ({areas.length})</h3>
              <button className="btn-primary btn-sm" onClick={openCreateAreaModal}>
                <Plus size={16} />
                Nueva
              </button>
            </div>
            {areas.length === 0 ? (
              <p className="no-areas">No hay áreas creadas</p>
            ) : (
              <ul className="areas-list">
                {areas.map(area => (
                  <li
                    key={area.id}
                    className={`area-item ${selectedArea?.id === area.id ? 'active' : ''}`}
                  >
                    <button
                      className="area-select"
                      onClick={() => setSelectedArea(area)}
                    >
                      <FolderOpen size={18} />
                      <span>{area.name}</span>
                      <small>{area.documentsCount || 0} docs</small>
                    </button>
                    <div className="area-actions">
                      <button onClick={() => openEditAreaModal(area)} title="Editar">
                        <Edit2 size={14} />
                      </button>
                      <button onClick={() => handleDeleteArea(area)} title="Eliminar">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Documents panel */}
          <div className="documents-panel">
            {selectedArea ? (
              <>
                <div className="documents-header">
                  <h3>
                    Documentos en "{selectedArea.name}"
                  </h3>
                  <div className="upload-controls">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,.txt,.docx,.doc,.md,.xlsx,.xls"
                      onChange={(e) => handleFileUpload(e.target.files[0])}
                      style={{ display: 'none' }}
                    />
                    <button
                      className="btn-primary"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                    >
                      {uploading ? (
                        <Loader size={18} className="loading-spinner" />
                      ) : (
                        <Upload size={18} />
                      )}
                      Subir Documento
                    </button>
                  </div>
                </div>

                {documents.length === 0 ? (
                  <div className="empty-documents">
                    <FileText size={48} />
                    <p>No hay documentos en esta área</p>
                    <button
                      className="btn-secondary"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload size={18} />
                      Subir primer documento
                    </button>
                  </div>
                ) : (
                  <div className="documents-list">
                    {documents.map(doc => (
                      <div key={doc.id} className="document-row">
                        <div className="document-info">
                          <FileText size={20} />
                          <div>
                            <span className="doc-name">{doc.filename}</span>
                            <span className="doc-meta">
                              {doc.chunks_count} fragmentos
                            </span>
                          </div>
                        </div>
                        <div className="document-actions">
                          <button
                            className="btn-icon"
                            onClick={() => openEditDocModal(doc)}
                            title="Editar contenido"
                          >
                            <Edit3 size={16} />
                          </button>
                          <button
                            className="btn-icon danger"
                            onClick={() => handleDeleteDocument(doc)}
                            title="Eliminar"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="no-area-selected">
                <FolderOpen size={48} />
                <p>Selecciona un área para ver sus documentos</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Area Modal */}
      {modalOpen && (
        <div className="modal-overlay" onClick={() => !saving && setModalOpen(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingArea ? 'Editar Área' : 'Nueva Área'}</h2>
              <button className="modal-close" onClick={() => setModalOpen(false)}>
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleAreaSubmit}>
              <div className="form-group">
                <label>Nombre</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ej: Soporte Técnico"
                  required
                />
              </div>
              <div className="form-group">
                <label>Descripción</label>
                <textarea
                  value={formData.description}
                  onChange={e => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Descripción del área..."
                  rows={3}
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setModalOpen(false)}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? <Loader size={16} className="loading-spinner" /> : null}
                  {editingArea ? 'Guardar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Document Modal */}
      {editDocModal && (
        <div className="modal-overlay" onClick={() => !saving && setEditDocModal(false)}>
          <div className="modal-content modal-large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <Edit3 size={24} />
                Editar: {editingDoc?.filename}
              </h2>
              <button className="modal-close" onClick={() => setEditDocModal(false)}>
                <X size={24} />
              </button>
            </div>
            <div className="modal-body">
              {loadingDoc ? (
                <div className="loading-content">
                  <Loader size={32} className="loading-spinner" />
                  <p>Cargando documento...</p>
                </div>
              ) : (
                <textarea
                  className="document-editor"
                  value={docContent}
                  onChange={e => setDocContent(e.target.value)}
                  disabled={saving}
                />
              )}
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setEditDocModal(false)}
                disabled={saving}
              >
                Cancelar
              </button>
              <button
                className="btn-primary"
                onClick={handleSaveDocument}
                disabled={saving || loadingDoc}
              >
                {saving ? <Loader size={16} className="loading-spinner" /> : <Save size={16} />}
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Area Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteAreaModal.open}
        title="Eliminar Área"
        message={`¿Estás seguro de eliminar el área "${deleteAreaModal.area?.name}"? Se eliminarán todos sus documentos. Esta acción no se puede deshacer.`}
        onConfirm={confirmDeleteArea}
        onCancel={() => setDeleteAreaModal({ open: false, area: null })}
      />

      {/* Delete Document Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteDocModal.open}
        title="Eliminar Documento"
        message={`¿Estás seguro de eliminar el documento "${deleteDocModal.doc?.filename}"? Esta acción no se puede deshacer.`}
        onConfirm={confirmDeleteDocument}
        onCancel={() => setDeleteDocModal({ open: false, doc: null })}
      />

      {/* Toast */}
      {toast && (
        <div className={`toast ${toast.type}`}>
          {toast.type === 'success' ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
          {toast.message}
        </div>
      )}
    </AdminLayout>
  );
}

export default AreasPage;
